/**
 * 瀏覽器推播開關的狀態判定（2026-08-29）
 *
 * 放在 shared 而非 client，是為了讓既有的 vitest 設定（只涵蓋 server/** 與 shared/**）
 * 能測到這段判定 —— 它是純函式、不碰 DOM。
 *
 * 原本的 UI 只有「啟用／停用」兩種按鈕，權限被拒、伺服器沒設金鑰、訂閱失敗
 * 全都靜默無提示，使用者按了看不出任何變化。這裡把所有情境攤開。
 */
export type PushStatus =
  /** 瀏覽器不支援 Web Push */
  | "unsupported"
  /** 正在處理（要求權限／註冊 service worker／送出訂閱） */
  | "working"
  /** 已啟用 */
  | "subscribed"
  /** 使用者曾拒絕權限；瀏覽器不會再詢問，須到瀏覽器設定改 */
  | "denied"
  /** 伺服器未設定 VAPID 金鑰，功能無法使用 */
  | "unconfigured"
  /** 尚未啟用，可以啟用 */
  | "idle";

/**
 * 通知權限。等同瀏覽器的 NotificationPermission，但這裡自己宣告 —— shared/
 * 也會被伺服器端的 tsconfig 編譯，那邊沒有 DOM 型別。
 */
export type PushPermission = "default" | "granted" | "denied" | "unsupported";

export interface PushStatusInput {
  supported: boolean;
  hasVapidKey: boolean;
  permission: PushPermission;
  isSubscribed: boolean;
  busy: boolean;
}

export function derivePushStatus(s: PushStatusInput): PushStatus {
  if (!s.supported || s.permission === "unsupported") return "unsupported";
  if (s.busy) return "working";
  if (s.isSubscribed) return "subscribed";
  if (s.permission === "denied") return "denied";
  if (!s.hasVapidKey) return "unconfigured";
  return "idle";
}

/** 只有這兩種狀態才該讓使用者點「啟用」 */
export function canEnablePush(status: PushStatus): boolean {
  return status === "idle";
}

/** 是否顯示為「已開啟」的樣式（綠色／勾勾） */
export function isPushOn(status: PushStatus): boolean {
  return status === "subscribed";
}
